import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    LinearProgress,
    Button,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import { errorTost, successTost } from "../../Services/Toast";
import { supabase } from "../../supabaseClient";

/* ------------------ Helpers ------------------ */

const formatIndianNumber = (num) => {
    if (num === undefined || num === null) return "0";
    const fixed = Number(num).toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const lastThree = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const formatted =
        rest.length > 0
            ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
            : lastThree;
    return `${formatted}.${decPart}`;
};

const formatCurrencyShort = (num) => {
    const n = Number(num || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} CR`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(n)}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const downloadCSV = (bookings, fileName = "AOP_Report") => {
    if (!bookings || bookings.length === 0) {
        alert("No data available for download!");
        return;
    }

    const headers = ["Project", "Value", "Brokerage", "Date"];

    const csvRows = [
        headers.join(","),
        ...bookings.map((row) => {
            return [
                row.project,
                formatIndianNumber(row.value),
                formatIndianNumber(row.incentive),
                formatDate(row.date),
            ]
                .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                .join(",");
        }),
    ].join("\n");

    const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/* ------------------ Animated Progress ------------------ */

const AnimatedProgressBar = ({ value }) => {
    const [animatedValue, setAnimatedValue] = useState(0);

    useEffect(() => {
        const safeValue = Number(value) || 0;
        const finalValue = safeValue > 0 ? Math.max(Math.min(safeValue, 100), 2) : 0;

        let start = 0;
        const duration = 1200;
        const stepTime = 16;
        const steps = duration / stepTime;
        const increment = finalValue / steps;

        const timer = setInterval(() => {
            start += increment;
            if (start >= finalValue) {
                setAnimatedValue(finalValue);
                clearInterval(timer);
            } else {
                setAnimatedValue(start);
            }
        }, stepTime);

        return () => clearInterval(timer);
    }, [value]);





    return (
        <Box sx={{ mt: 1.5 }}>
            <LinearProgress
                variant="determinate"
                value={animatedValue}
                sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "rgba(255,255,255,0.18)",
                    "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #ffffff, #E9D5FF)",
                        transition: "transform 1s ease, width 1s ease",
                    },
                }}
            />
        </Box>
    );
};

const CardBox = ({ children, sx = {} }) => (
    <Card
        sx={{
            p: 2.5,
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
            border: "1px solid #EEF2F7",
            background: "#FFFFFF",
            ...sx,
        }}
    >
        {children}
    </Card>
);

/* ------------------ Main ------------------ */

export default function AOPDetailsPage({ onBack, selectedPlan, cpId }) {
    console.log("Selected Plan:", selectedPlan);
    console.log("CP ID:", cpId);
    const [aopData, setAopData] = useState({
        achievement: 0,
        sold: 0,
        incentive: 0,
        target: 50,
        projects: [],
        bookings: [],
    });

    const [slabs, setSlabs] = useState([]);
    const [animatedBars, setAnimatedBars] = useState([]);
    const [donutPercentages, setDonutPercentages] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [paidProjects, setPaidProjects] = useState({});
    const [withdrawLoading, setWithdrawLoading] = useState(false);

    useEffect(() => {
        if (!selectedPlan) return;
        setSlabs(selectedPlan.slabs || []);
        const rows = selectedPlan.rows || [];
        transformData(rows, selectedPlan.slabs || []);
    }, [selectedPlan]);

    const transformData = (rows, slabsData = []) => {
        let totalSold = 0;
        let totalIncentive = 0;
        const projectMap = {};
        const bookings = [];

        rows.forEach((row, i) => {
            const value = Number(row.booking_value || 0);
            const incentive = Number(row.aop_amount || 0);
            const projectName = row.Projects?.projectName || "Unknown";

            totalSold += value;
            totalIncentive += incentive;

            if (!projectMap[projectName]) projectMap[projectName] = 0;
            projectMap[projectName] += value;

            // bookings.push({
            //     id: row.booking_id || `BK-0${50 + i}`,
            //     project: projectName,
            //     value,
            //     incentive,
            //     date: row.created_at,
            //     enablePayout: row.enablePayout,
            // });

            bookings.push({
                id: row.booking_id || `BK-0${50 + i}`,
                project: projectName,
                shortCode: row.Projects?.projectShortCode || null,
                projectId: row.Projects?.projectId || row.project_id,
                value,
                incentive,
                date: row.created_at,
                enablePayout: row.enablePayout,
                isCpPaymentEntryDone: row.isCpPaymentEntryDone,
            });
        });

        const slabMax = slabsData.length
            ? Math.max(...slabsData.map((s) => Number(s.from_value)))
            : 50;

        const target = slabMax > 0 ? slabMax : 50;
        const achievement = target ? Math.min((totalSold / target) * 100, 100) : 0;

        // const projects = Object.keys(projectMap).map((key) => ({
        //     name: key,
        //     value: projectMap[key],
        // }));


        const projectMapWithId = {};

        rows.forEach((row) => {
            const projectName = row.Projects?.projectName || "Unknown";
            const projectId = row.Projects?.projectId || row.project_id;
            const shortCode = row.Projects?.projectShortCode || null;

            if (!projectMapWithId[projectName]) {
                projectMapWithId[projectName] = {
                    value: 0,
                    id: projectId,
                    shortCode,
                };
            }

            projectMapWithId[projectName].value += Number(row.booking_value || 0);
        });

        const projects = Object.keys(projectMapWithId).map((key) => ({
            name: key,
            value: projectMapWithId[key].value,
            id: projectMapWithId[key].id,
            shortCode: projectMapWithId[key].shortCode,
        }));

        setAopData({
            achievement,
            sold: totalSold,
            incentive: totalIncentive,
            target,
            projects,
            bookings,
        });
    };

    const maxValue = Math.max(...aopData.projects.map((d) => d.value), 1);
    const colors = ["#4F46E5", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6"];

    const brokerageData = useMemo(
        () =>
            aopData.projects.map((p, i) => ({
                label: p.name,
                shortCode: p.shortCode,
                value: p.value,
                color: colors[i % colors.length],
            })),
        [aopData.projects]
    );

    /* -------- Animate Bars -------- */
    useEffect(() => {
        const values = aopData.projects.map((item) => (item.value / maxValue) * 100);
        setAnimatedBars(values.map(() => 0));

        const timeout = setTimeout(() => {
            setAnimatedBars(values);
        }, 200);

        return () => clearTimeout(timeout);
    }, [aopData.projects, maxValue]);

    /* -------- Animate Donut -------- */
    useEffect(() => {
        const total = brokerageData.reduce((acc, item) => acc + item.value, 0);
        const percentages = brokerageData.map((item) => (item.value / (total || 1)) * 100);
        setDonutPercentages(percentages.map(() => 0));

        const timeout = setTimeout(() => {
            setDonutPercentages(percentages);
        }, 250);

        return () => clearTimeout(timeout);
    }, [brokerageData]);

    const totalDistribution = donutPercentages.reduce((acc, item) => acc + item, 0);
    let cumulative = 0;
    const gradient = brokerageData
        .map((item, i) => {
            const val = donutPercentages[i] || 0;
            const start = cumulative;
            const end = cumulative + val;
            cumulative = end;
            return `${item.color} ${start}% ${end}%`;
        })
        .join(", ");

    const currentSlab =
        slabs.find(
            (slab) =>
                aopData.sold >= Number(slab.from_value) &&
                aopData.sold <= Number(slab.to_value)
        ) || slabs[0];

    const nextSlab = slabs.find((slab) => aopData.sold < Number(slab.from_value));
    const nextTarget = nextSlab?.percentage || currentSlab?.percentage || 0;
    const amountNeeded = nextSlab ? Math.max(0, Number(nextSlab.from_value) - aopData.sold) : 0;


    const getShortProjectName = (name = "") => {
        if (!name) return "-";

        const words = name.trim().split(" ");

        // 1 word → return first 6 chars
        if (words.length === 1) return words[0].slice(0, 6);

        // 2 words → first 3 + first 3
        if (words.length === 2)
            return `${words[0].slice(0, 3)} ${words[1].slice(0, 3)}`;

        // 3+ words → initials (best UX)
        return words.map((w) => w[0]).join("").toUpperCase();
    };

    const triggerCpPayment = async (payload) => {
        console.log("🚀 Triggering CP Payment with payload:", payload);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SEND_OTP_MESSAGE_A2P_SERVICE}api/cp-payment/trigger-cp-payment`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Something went wrong");
            }

            return data;

        } catch (err) {
            console.error("❌ API Error:", err);
            throw err;
        }
    };

    useEffect(() => {
        const initialPaid = {};

        aopData.bookings.forEach((b) => {
            if (b.isCpPaymentEntryDone) {
                initialPaid[b.projectId] = true;
            }
        });

        setPaidProjects(initialPaid);
    }, [aopData.bookings]);

    return (
        <Box
            sx={{
                height: { xs: "80vh", md: "90vh" },
                overflowY: "auto",
                overflowX: "hidden",
                background: "#F5F7FB",
                pb: { xs: `calc(3rem + env(safe-area-inset-bottom))`, md: 3 },
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.8,
                    // background: "#FFFFFF",
                    borderBottom: "1px solid #E5E7EB",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                    onClick={onBack}
                >
                    <ArrowBackIcon sx={{ mr: 1, fontSize: 20, color: "#374151" }} />
                    <Box>
                        <Typography fontWeight={700} fontSize={{ xs: 15, sm: 18 }} color="#111827" lineHeight={1.2}>
                            {selectedPlan?.plan?.financial_year || "AOP Plan"}
                        </Typography>
                        <Typography fontSize={{ xs: 11, sm: 12 }} color="#9CA3AF" mt={0.2}>
                            📅 {formatDate(selectedPlan?.plan?.validity_start_date)} –{" "}
                            {formatDate(selectedPlan?.plan?.validity_end_date)}
                        </Typography>
                        <Typography fontSize={{ xs: 10, sm: 11 }} color="#B45309" mt={0.4}>
                            Agreement done lead only
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2.5,
                        fontSize: { xs: 10, sm: 12 },
                        fontWeight: 600,
                        color: "#374151",
                        borderColor: "#D1D5DB",
                        px: 2,
                        py: 0.8,
                        "&:hover": { borderColor: "#9CA3AF", background: "#F9FAFB" },
                    }}
                    onClick={() => downloadCSV(aopData.bookings, "AOP_Report")}
                >
                    Download Excel
                </Button>
            </Box>

            {/* CONTENT */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: { xs: 2, md: 3 },
                    py: 2.5,
                    pb: { xs: 14, sm: 15 },
                }}
            >
                {/* HERO */}
                <Box
                    sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 4,
                        background: "linear-gradient(90deg, #3B4CCA 0%, #6D28D9 100%)",
                        color: "#fff",
                        mb: 2.5,
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography fontSize={11} fontWeight={700} sx={{ opacity: 0.9, mb: 0.6 }}>
                                OVERALL ACHIEVEMENT
                            </Typography>

                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography fontSize={{ xs: 34, sm: 42 }} fontWeight={800} lineHeight={1}>
                                    {aopData.achievement.toFixed(0)}%
                                </Typography>
                                <Typography fontSize={13} sx={{ opacity: 0.9 }}>
                                    of target
                                </Typography>
                            </Box>

                            <AnimatedProgressBar value={aopData.achievement} />

                            <Box mt={1.2} display="flex" justifyContent="space-between">
                                <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                                    ₹0
                                </Typography>
                                <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                                    Target {formatCurrencyShort(aopData.target)}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.2,
                                        background: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        px: 2,
                                        py: 1.2,
                                        borderRadius: 3,
                                    }}
                                >
                                    <Box>
                                        <Typography fontSize={10} sx={{ opacity: 0.8 }} textAlign="right">
                                            CURRENT SLAB
                                        </Typography>
                                        <Typography fontSize={22} fontWeight={800} textAlign="right">
                                            {currentSlab?.percentage?.toFixed(0) || 0}%
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "50%",
                                            background: "rgba(255,255,255,0.14)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 18,
                                        }}
                                    >
                                        🏅
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* STATS */}
                <Grid container spacing={2} mb={2.5}>
                    {[
                        { title: "TOTAL SOLD", value: formatCurrencyShort(aopData.sold), icon: "📈" },
                        { title: "INCENTIVE EARNED", value: formatCurrencyShort(aopData.incentive), icon: "🏆", isIncentive: true },
                        { title: "FY TARGET", value: formatCurrencyShort(aopData.target), icon: "🎯" },
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <CardBox sx={{ minHeight: 110 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            background: "#EEF2FF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 14,
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography fontSize={11} fontWeight={700} color="#9CA3AF">
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Typography
                                    fontWeight={800}
                                    fontSize={{ xs: 24, sm: 28 }}
                                    color={item.isIncentive ? "#F59E0B" : "#111827"}
                                >
                                    {item.value}
                                </Typography>

                                {item.isIncentive && (
                                    <Typography fontSize={12} color="#9CA3AF" mt={0.8}>
                                        🏦 Payable at FY End
                                    </Typography>
                                )}
                            </CardBox>
                        </Grid>
                    ))}
                </Grid>

                {/* WHAT'S NEXT */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "#FEFCE8",
                        border: "1px solid #FDE68A",
                        mb: 2.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.2}>
                        <Box
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                background: "#FBBF24",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                            }}
                        >
                            ⚡
                        </Box>
                        <Box>
                            <Typography fontSize={13} fontWeight={700} color="#92400E">
                                What’s Next?
                            </Typography>
                            <Typography fontSize={12} color="#78716C">
                                Sell {formatCurrencyShort(amountNeeded)} more to unlock the{" "}
                                {nextTarget}% slab
                            </Typography>
                        </Box>
                    </Box>

                    <Box textAlign="right">
                        <Typography fontSize={10} color="#A16207" fontWeight={700}>
                            NEXT LEVEL
                        </Typography>
                        <Typography fontSize={22} fontWeight={800} color="#F59E0B">
                            {nextTarget}%
                        </Typography>
                    </Box>
                </Box>

                {/* SLABS */}
                <CardBox sx={{ mb: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                            <Typography fontWeight={700} fontSize={14} color="#111827">
                                Your Milestone Journey
                            </Typography>
                            <Typography fontSize={11} color="#9CA3AF">
                                Level {Math.max(1, slabs.findIndex((s) => aopData.sold < s.to_value) + 1 || slabs.length)} of {slabs.length}
                            </Typography>
                        </Box>
                        <Box textAlign="right">
                            <Typography fontSize={10} color="#9CA3AF" fontWeight={700}>
                                NEXT TARGET
                            </Typography>
                            <Typography fontSize={18} fontWeight={800} color="#4F46E5">
                                {nextTarget}%
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 30,
                                left: 0,
                                right: 0,
                                height: 2,
                                background: "#E5E7EB",
                                zIndex: 0,
                            }}
                        />

                        {slabs.map((slab, index) => {
                            const isCompleted = aopData.sold >= Number(slab.to_value);
                            const isCurrent =
                                aopData.sold >= Number(slab.from_value) &&
                                aopData.sold < Number(slab.to_value);

                            return (
                                <Box
                                    key={slab.id}
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        minWidth: 120,
                                        textAlign: "center",
                                        px: 1,
                                    }}
                                >
                                    <Typography
                                        fontSize={12}
                                        fontWeight={700}
                                        color={isCurrent ? "#4F46E5" : isCompleted ? "#16A34A" : "#9CA3AF"}
                                        mb={1}
                                    >
                                        {slab.percentage?.toFixed(0)}%
                                    </Typography>

                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            mx: "auto",
                                            background: isCompleted
                                                ? "#4F46E5"
                                                : isCurrent
                                                    ? "#FBBF24"
                                                    : "#F3F4F6",
                                            border: isCompleted || isCurrent ? "none" : "1px solid #D1D5DB",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: isCompleted || isCurrent ? "#fff" : "#9CA3AF",
                                            fontSize: 13,
                                            boxShadow: isCurrent ? "0 0 0 6px rgba(251,191,36,0.18)" : "none",
                                            transition: "all 0.5s ease",
                                        }}
                                    >
                                        {isCompleted ? "✓" : isCurrent ? "→" : "🔒"}
                                    </Box>

                                    <Typography fontSize={10} color="#6B7280" mt={1} fontWeight={700}>
                                        {formatCurrencyShort(slab.from_value)}
                                    </Typography>
                                    <Typography fontSize={9} color="#9CA3AF">
                                        {isCompleted ? "Unlocked" : isCurrent ? "Next" : "Locked"}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2.5,
                            background: "#FFFBEB",
                            border: "1px solid #FDE68A",
                        }}
                    >
                        <Typography fontSize={12} color="#92400E" fontWeight={700}>
                            Reach {nextTarget}% to unlock {formatCurrencyShort(nextSlab?.from_value || 0)} reward
                        </Typography>
                        <Typography fontSize={11} color="#78716C">
                            Keep pushing — you’re {formatCurrencyShort(amountNeeded)} away from the top!
                        </Typography>
                    </Box>
                </CardBox>

                {/* CHARTS */}
                <Grid container spacing={2} mb={2.5}>
                    {/* BAR CHART */}
                    <Grid item xs={12} md={6}>
                        <CardBox>
                            <Typography mb={2} fontSize={14} fontWeight={700} color="#111827">
                                Project-wise Value (CR)
                            </Typography>

                            <Box
                                sx={{
                                    height: { xs: 220, sm: 250 },
                                    display: "flex",
                                    alignItems: "flex-end",
                                    gap: 2,
                                    borderBottom: "1.5px solid #E5E7EB",
                                    px: 1,
                                    overflowX: "auto",
                                    pb: 1,
                                }}
                            >
                                {aopData.projects.map((item, index) => {
                                    const heightPercent = animatedBars[index] || 0;
                                    return (
                                        <Tooltip
                                            key={index}
                                            arrow
                                            placement="top"
                                            title={
                                                <Box sx={{ p: 0.5 }}>
                                                    <Typography fontSize={12} fontWeight={700} color="#fff">
                                                        {item.name || "—"}
                                                    </Typography>
                                                    <Typography fontSize={11} color="#86EFAC" fontWeight={700} mt={0.2}>
                                                        Sold Value: {formatCurrencyShort(item.value)}
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                        <Box
                                            sx={{
                                                flex: "1 0 120px",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                height: "100%",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Typography fontSize={11} mb={0.8} color="#374151" fontWeight={700}>
                                                {formatCurrencyShort(item.value)}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    width: "75%",
                                                    height: `${heightPercent}%`,
                                                    background: "linear-gradient(180deg, #4F46E5 0%, #4338CA 100%)",
                                                    borderRadius: "10px 10px 0 0",
                                                    minHeight: "10px",
                                                    transition: "height 1.2s cubic-bezier(0.22, 1, 0.36, 1)",
                                                    boxShadow: "0 8px 18px rgba(79,70,229,0.22)",
                                                    "&:hover": { filter: "brightness(1.2)" },
                                                }}
                                            />

                                            <Typography
                                                fontSize={11}
                                                mt={1}
                                                textAlign="center"
                                                color="#6B7280"
                                                fontWeight={500}
                                            >
                                                {item.shortCode || getShortProjectName(item.name)}
                                            </Typography>
                                        </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </CardBox>
                    </Grid>

                    {/* DONUT CHART */}
                    <Grid item xs={12} md={6}>
                        <CardBox>
                            <Typography mb={2} fontSize={14} fontWeight={700} color="#111827">
                                Brokerage Distribution
                            </Typography>

                            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                                <Box
                                    sx={{
                                        width: { xs: 140, sm: 170 },
                                        height: { xs: 140, sm: 170 },
                                        borderRadius: "50%",
                                        background:
                                            brokerageData.length > 0
                                                ? `conic-gradient(${gradient})`
                                                : "#E5E7EB",
                                        position: "relative",
                                        transition: "all 1.2s ease",
                                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: { xs: 72, sm: 92 },
                                            height: { xs: 72, sm: 92 },
                                            borderRadius: "50%",
                                            background: "#fff",
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <Typography fontSize={10} color="#9CA3AF" fontWeight={700}>
                                            TOTAL
                                        </Typography>
                                        <Typography fontSize={14} fontWeight={800} color="#111827">
                                            {formatCurrencyShort(aopData.sold)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box mt={2.5}>
                                {brokerageData.map((item, i) => (
                                    <Tooltip
                                        key={i}
                                        arrow
                                        placement="right"
                                        title={
                                            <Box sx={{ p: 0.5 }}>
                                                <Typography fontSize={12} fontWeight={700} color="#fff">
                                                    {item.label || "—"}
                                                </Typography>
                                                <Typography fontSize={11} color="#86EFAC" fontWeight={700} mt={0.2}>
                                                    Sold Value: {formatCurrencyShort(item.value)}
                                                </Typography>
                                                <Typography fontSize={11} color="rgba(255,255,255,0.7)" mt={0.2}>
                                                    Share: {donutPercentages[i] ? donutPercentages[i].toFixed(1) : "0"}%
                                                </Typography>
                                            </Box>
                                        }
                                    >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={1}
                                        sx={{
                                            cursor: "pointer",
                                            px: 0.8,
                                            py: 0.4,
                                            borderRadius: 1.5,
                                            transition: "background 0.2s",
                                            "&:hover": { background: "rgba(0,0,0,0.05)" },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: "50%",
                                                    background: item.color,
                                                    mr: 1,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography fontSize={12} color="#374151">
                                                {item.shortCode || getShortProjectName(item.label)}
                                            </Typography>
                                        </Box>
                                        <Typography fontSize={12} fontWeight={700} color="#111827">
                                            {formatCurrencyShort(item.value)}
                                        </Typography>
                                    </Box>
                                    </Tooltip>
                                ))}
                            </Box>
                        </CardBox>
                    </Grid>
                </Grid>

                {/* TABLE */}

                {/* PROJECT-WISE BREAKDOWN */}
                <CardBox sx={{ mb: 2.5 }}>
                    <Typography mb={2} fontSize={14} fontWeight={700} color="#111827">
                        Project-wise Breakdown
                    </Typography>

                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {["PROJECT", "VALUE", "BROKERAGE", "BOOKINGS", "ACTIONS"].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "#6B7280",
                                                letterSpacing: 0.4,
                                                borderBottom: "1px solid #F3F4F6",
                                                pb: 1.2,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {aopData.projects.map((proj, i) => {
                                    const projectBookings = aopData.bookings.filter(
                                        (b) => b.project === proj.name
                                    );

                                    const totalBrokerage = projectBookings.reduce(
                                        (acc, b) => acc + (b.incentive || 0),
                                        0
                                    );

                                    return (
                                        <TableRow
                                            key={i}
                                            sx={{
                                                "&:last-child td": { border: 0 },
                                                "&:hover": { background: "#F9FAFB" },
                                                "& td": { borderBottom: "1px solid #F9FAFB" },
                                                transition: "0.2s ease",
                                            }}
                                        >
                                            {/* PROJECT */}
                                            <TableCell
                                                sx={{ fontSize: 13, color: "#374151" }}
                                                title={proj.name}
                                            >
                                                {proj.shortCode || getShortProjectName(proj.name)}
                                            </TableCell>

                                            {/* VALUE */}
                                            <TableCell
                                                sx={{ fontSize: 13, color: "#111827", fontWeight: 700 }}
                                            >
                                                {formatCurrencyShort(proj.value)}
                                            </TableCell>

                                            {/* BROKERAGE */}
                                            <TableCell
                                                sx={{ fontSize: 13, color: "#10B981", fontWeight: 800 }}
                                            >
                                                {formatCurrencyShort(totalBrokerage)}
                                            </TableCell>

                                            {/* COUNT */}
                                            <TableCell
                                                sx={{ fontSize: 13, color: "#6B7280" }}
                                            >
                                                {projectBookings.length}
                                            </TableCell>

                                            {/* ACTION (optional to match structure) */}
                                            <TableCell sx={{ fontSize: 13, color: "#6B7280" }}>
                                                {projectBookings.some(b => b.enablePayout) && (
                                                    <Button
                                                        variant="contained"
                                                        disabled={paidProjects[proj.id] || totalBrokerage === 0}
                                                        onClick={() => {
                                                            if (paidProjects[proj.id]) return;

                                                            setSelectedProject(proj);
                                                            setConfirmOpen(true);
                                                        }}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontSize: { xs: 11, sm: 12 },
                                                            fontWeight: 400,
                                                            borderRadius: 2,
                                                            px: 3,
                                                            py: 1,
                                                        }}
                                                    >
                                                        {paidProjects[proj.id] ? "Already Withdrawn" : "Withdraw AOP"}
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                </CardBox>
                <CardBox>
                    <Typography mb={2} fontSize={14} fontWeight={700} color="#111827">
                        Booking-wise Breakdown
                    </Typography>

                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {["PROJECT", "VALUE", "BROKERAGE", "AGREEMENT DATE"].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "#6B7280",
                                                letterSpacing: 0.4,
                                                borderBottom: "1px solid #F3F4F6",
                                                pb: 1.2,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {aopData.bookings.map((row, i) => (
                                    <TableRow
                                        key={i}
                                        sx={{
                                            "&:last-child td": { border: 0 },
                                            "&:hover": { background: "#F9FAFB" },
                                            "& td": { borderBottom: "1px solid #F9FAFB" },
                                            transition: "0.2s ease",
                                        }}
                                    >

                                        <TableCell
                                            sx={{ fontSize: 13, color: "#374151" }}
                                            title={row.project} // 👈 full name on hover
                                        >
                                            {row.shortCode || getShortProjectName(row.project)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                                            {formatCurrencyShort(row.value)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: "#10B981", fontWeight: 800 }}>
                                            {formatCurrencyShort(row.incentive)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: "#6B7280" }}>
                                            {formatDate(row.agreement_date)}
                                        </TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </CardBox>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Withdrawal</DialogTitle>

                <DialogContent>
                    <Typography fontSize={14}>
                        Are you sure you want to withdraw AOP payment for{" "}
                        <strong>{selectedProject?.name}</strong>?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        disabled={withdrawLoading}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={withdrawLoading}
                        onClick={async () => {
                            try {
                                setWithdrawLoading(true);

                                const payload = {
                                    cpId: cpId,
                                    aopId: selectedPlan?.plan?.id,
                                    popId: null,
                                    projectId: selectedProject?.id,
                                };

                                await triggerCpPayment(payload);

                                // ✅ Update UI immediately
                                setPaidProjects((prev) => ({
                                    ...prev,
                                    [selectedProject.id]: true,
                                }));

                                successTost("Withdrawal request completed successfully");
                                setConfirmOpen(false);
                            } catch (error) {
                                console.error(error);
                                errorTost("Failed to withdraw AOP payment");
                            } finally {
                                setWithdrawLoading(false);
                            }
                        }}
                    >
                        {withdrawLoading ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}